import React from 'react';
import { createRoot } from 'react-dom/client';
import initUserMenu from '@PROJECT_ROOT/assets/responsive-frontend/js/userMenu';
import initFetchWithOneRetry from '@PROJECT_ROOT/assets/responsive-frontend/js/services/fetchWithOneRetryTwigWrap';
import NewsletterOffer from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/NewsletterOffer';
import ZarazConsent from '@PROJECT_ROOT/assets/responsive-frontend/js/services/zarazConsent';
import setupBootstrapModalsForBackButtonFunctionality from '@PROJECT_ROOT/assets/responsive-frontend/js/services/backButtonCloseBootstrapModals';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import { dispatchTrackingEvent, LANGUAGE_SWITCHER_HEADER_CLICK } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import screenService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/screenService';
// Utils
import { runDeferredInBrowser } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/utils';

function trackEvent(elementId, eventType, eventName) {
    const element = document.getElementById(elementId);
    if (element) {
        element.addEventListener(eventType, () => {
            if (!window.AppConfig.isFrame) dispatchTrackingEvent(eventName);
        });
    }
}

function initZaraz() {
    const zarazConsent = document.getElementById('zarazConsent');
    if (zarazConsent) {
        const root = createRoot(zarazConsent);
        root.render(<ZarazConsent />);
    }
}

function initFooter(temporaryDisconnectVRM) {
    if (IS_FRAME_SERVICE) {
        return;
    }
    initBindCookieSettingsModalLink();
    initNewsletterFooterForm();
    if (!temporaryDisconnectVRM) {
        initNewsletterPopup();
    }
    trackEvent('footerContactLink', 'click', 'ContactFormFooterLinkClick');
}

function initBindCookieSettingsModalLink() {
    const consentButton = document.getElementById('open-consent-modal');
    if (consentButton) {
        consentButton.addEventListener('click', () => {
            const event = new CustomEvent('openConsentModal');
            window.dispatchEvent(event);
        });
    }
}

function initNewsletterFooterForm() {
    const newsletterFooterForm = document.getElementById('newsletterFooterReact');
    if (!newsletterFooterForm) return;
    let isInitialized = false;
    let isLoading = false;
    let observer = null;
    let fallbackTimeout = null;

    async function mountNewsletterFooterForm() {
        if (isInitialized || isLoading) return;
        isLoading = true;
        try {
            const { default: NewsletterFooterForm } = await import(
                /* webpackChunkName: "responsiveFrontendNewsletterFooterFormFromAfterContentComponent" */
                '@PROJECT_ROOT/assets/responsive-frontend/js/parts/newsletterFooterForm'
            );
            if (observer) {
                observer.disconnect();
            }
            if (fallbackTimeout) {
                clearTimeout(fallbackTimeout);
            }
            const root = createRoot(newsletterFooterForm);
            root.render(<NewsletterFooterForm />);
            isInitialized = true;
        } finally {
            isLoading = false;
        }
    }

    observer = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) {
                mountNewsletterFooterForm();
            }
        },
        {
            rootMargin: '300px 0px',
        },
    );

    observer.observe(newsletterFooterForm);

    // TODO: Centralizirat funkciju za fallback učitavanje ako se bude ponavljanja
    fallbackTimeout = setTimeout(() => {
        mountNewsletterFooterForm();
    }, 10000);
}

function initNewsletterPopup() {
    const newsletterPopupReact = document.getElementById('newsletterPopupReact');

    if (!newsletterPopupReact) return;

    const showPopupDelayMs = 30000;
    const showPopupScrollThreshold = 0.5;
    const exitIntentTimeoutMs = 15000;

    let isTriggered = false;
    let popupTimeout = null;
    let exitIntentTimeout = null;
    let scrollAnimationFrame = null;

    function cleanup() {
        document.removeEventListener('mouseout', handleExitIntent);
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(popupTimeout);
        clearTimeout(exitIntentTimeout);
        if (scrollAnimationFrame !== null) {
            cancelAnimationFrame(scrollAnimationFrame);
        }
    }

    async function mountNewsletterPopup() {
        if (isTriggered) return;

        isTriggered = true;
        cleanup();
        const { default: NewsletterPopup } = await import(
            /* webpackChunkName: "responsiveFrontendNewsletterPopupFromAfterContentComponent" */
            '@PROJECT_ROOT/assets/responsive-frontend/js/parts/NewsletterPopup'
        );
        const root = createRoot(newsletterPopupReact);
        root.render(<NewsletterPopup />);
    }

    function handleExitIntent(e) {
        if (e.clientY <= 0) mountNewsletterPopup();
    }

    function handleScroll() {
        if (scrollAnimationFrame !== null) return;

        scrollAnimationFrame = requestAnimationFrame(() => {
            scrollAnimationFrame = null;
            const scrollTop = window.scrollY;
            const viewportHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollPercentage = (scrollTop + viewportHeight) / documentHeight;

            if (scrollPercentage >= showPopupScrollThreshold) {
                mountNewsletterPopup();
            }
        });
    }

    const isDesktopView = screenService.isDesktopView();

    if (isDesktopView) {
        exitIntentTimeout = setTimeout(() => {
            document.addEventListener('mouseout', handleExitIntent);
        }, exitIntentTimeoutMs);
    } else {
        popupTimeout = setTimeout(
            mountNewsletterPopup,
            showPopupDelayMs,
        );

        window.addEventListener(
            'scroll',
            handleScroll,
            { passive: true },
        );
    }
}

async function initLanguageSwitcher() {
    try {
        document.addEventListener('click', (event) => {
            const langSelects = document.querySelectorAll('.langSelectTwo');
            const langButtons = document.querySelectorAll('.langButtonTwo');

            let clickedInside = false;

            langButtons.forEach((button, index) => {
                const select = langSelects[index];
                if (button.contains(event.target) || (select && select.contains(event.target))) {
                    clickedInside = true;
                }
            });

            if (!clickedInside) {
                document.querySelectorAll('.navbar .langSelectTwo').forEach((select) => {
                    select.style.display = 'none';
                });
            }
        });
        document.querySelectorAll('.langButtonTwo').forEach((button) => {
            button.addEventListener('click', () => {
                const langSelect = document.querySelector('.langSelectTwo');
                langSelect.style.display = (langSelect.style.display === 'block') ? 'none' : 'block';
                if (!window.AppConfig.isFrame) dispatchTrackingEvent(LANGUAGE_SWITCHER_HEADER_CLICK);
            });
        });
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Greška u JS izbornika jezika:', error?.message);
    }
}

function initNewsletterOffer() {
    const newsletterOffer = document.getElementById('newsletterOfferReact');
    if (newsletterOffer) {
        const root = createRoot(newsletterOffer);
        root.render(<NewsletterOffer />);
    }
}

function onReady(fn) {
    if (document.readyState !== 'loading') {
        fn();
    } else {
        document.addEventListener('DOMContentLoaded', fn);
    }
}

let isInitializedAfterContentComponent = false;

// NAPOMENA: "temporaryDisconnectVRM" je dodano samo kao brzo rješenje da se newsletter popup ne inicijalizira na VRM page-u.
// TODO: "temporaryDisconnectVRM" ce biti uklonjen ili dorađen nakon što završi VRM.
export default function initAfterContentComponent(temporaryDisconnectVRM = false) {
    if (isInitializedAfterContentComponent) return;
    isInitializedAfterContentComponent = true;

    initZaraz();
    initFetchWithOneRetry();

    // DESC: NON-CRITICAL inicijalizacije (pokušaj da ih izbacimo iz inicijalnog render-a/hydration-e)
    runDeferredInBrowser(() => {
        onReady(setupBootstrapModalsForBackButtonFunctionality);
        initUserMenu();
        initFooter(temporaryDisconnectVRM);
        initLanguageSwitcher();
        initNewsletterOffer();
    });
}
